/**
 * API Client
 * 
 * Type-safe API client for Kirjuri backend.
 * Features:
 * - Automatic JSON parsing
 * - Centralized error handling
 * - Request deduplication
 * - Exponential backoff retry
 * - Request cancellation support
 */

import type { RequestOptions } from './types';
import {
  SESSION_COOKIE_NAME,
  API_MAX_RETRY_ATTEMPTS,
  API_RETRY_BASE_DELAY_MS,
  API_RETRY_MAX_DELAY_MS,
} from '../config/constants';
import { reportApiError } from './errorReporting';

export const API_BASE = import.meta.env.VITE_API_URL || 'https://kirjuri-backend.tuomas-508.workers.dev';

// In-flight request tracking for deduplication
const inFlightRequests = new Map<string, Promise<unknown>>();

// Get auth token from cookie
function getAuthToken(): string | null {
  const match = document.cookie.match(new RegExp(`${SESSION_COOKIE_NAME}=([^;]+)`));
  return match ? match[1] : null;
}

// Generate request key for deduplication
function getRequestKey(method: string, path: string, body?: unknown): string {
  return `${method}:${path}:${body ? JSON.stringify(body) : ''}`;
}

// Delay helper for retry
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// Check if error is retriable
function isRetriableError(error: unknown): boolean {
  if (error instanceof TypeError) {
    // Network errors
    return true;
  }
  if (error instanceof Response) {
    // Retry on 5xx errors or 429 (rate limit)
    return error.status >= 500 || error.status === 429;
  }
  return false;
}

export class ApiClient {
  private baseUrl: string;
  private defaultHeaders: Record<string, string>;

  constructor(baseUrl: string = API_BASE) {
    this.baseUrl = baseUrl;
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };
  }

  /**
   * Make an HTTP request
   */
  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    options: RequestOptions = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${path}`;
    const token = getAuthToken();
    
    const headers: Record<string, string> = {
      ...this.defaultHeaders,
      ...options.headers,
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const config: RequestInit = {
      method,
      headers,
      credentials: 'include', // Include cookies
      signal: options.signal,
    };

    if (body && method !== 'GET') {
      config.body = JSON.stringify(body);
    }

    // Request deduplication for GET requests
    if (method === 'GET' && options.retry !== false) {
      const key = getRequestKey(method, path, body);
      const inFlight = inFlightRequests.get(key);
      if (inFlight) {
        return inFlight as Promise<T>;
      }
    }

    const requestPromise = this.executeWithRetry<T>(url, config, options);
    
    // Track in-flight request
    if (method === 'GET') {
      const key = getRequestKey(method, path, body);
      inFlightRequests.set(key, requestPromise);
      requestPromise.finally(() => {
        inFlightRequests.delete(key);
      });
    }

    return requestPromise;
  }

  /**
   * Execute request with exponential backoff retry
   */
  private async executeWithRetry<T>(
    url: string,
    config: RequestInit,
    options: RequestOptions,
    attempt: number = 1
  ): Promise<T> {
    try {
      const response = await fetch(url, config);
      
      // Handle HTTP errors
      if (!response.ok) {
        // Handle specific status codes
        if (response.status === 401) {
          // Redirect to login
          window.location.href = '/login';
          throw new Error('Session expired. Please log in again.');
        }
        
        if (response.status === 403) {
          throw new Error('Access denied. You do not have permission to perform this action.');
        }

        // Try to parse error response
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        let errorData: unknown = null;
        try {
          errorData = await response.clone().json();
          if (errorData && typeof errorData === 'object' && 'error' in errorData) {
            errorMessage = String((errorData as Record<string, unknown>).error);
          }
        } catch {
          // Ignore JSON parse errors
        }

        // Report API errors (after retries exhausted or non-retriable).
        // Skip if this IS the error-reporting endpoint (prevents recursive loop)
        // and skip 429 (rate-limited — reporting it would just add more requests).
        const isErrorEndpoint = url.includes('/logs/frontend-errors');
        const isRateLimitError = response.status === 429;
        if (!isErrorEndpoint && !isRateLimitError &&
            (attempt >= API_MAX_RETRY_ATTEMPTS || !isRetriableError(response) || options.retry === false)) {
          reportApiError(response.status, response.statusText, url, errorData);
        }

        // Check if we should retry
        if (isRetriableError(response) && attempt < API_MAX_RETRY_ATTEMPTS && options.retry !== false) {
          const backoffDelay = Math.min(
            API_RETRY_BASE_DELAY_MS * Math.pow(2, attempt - 1),
            API_RETRY_MAX_DELAY_MS
          );
          await delay(backoffDelay);
          return this.executeWithRetry<T>(url, config, options, attempt + 1);
        }

        throw new Error(errorMessage);
      }

      // Parse response
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        return await response.json() as T;
      }

      // For non-JSON responses (e.g., HTML for auth verify)
      return await response.text() as unknown as T;

    } catch (error) {
      // Handle abort
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error;
      }

      // Report network errors after retries exhausted
      if (attempt >= API_MAX_RETRY_ATTEMPTS || !isRetriableError(error) || options.retry === false) {
        if (error instanceof TypeError) {
          // Network error
          reportApiError(0, 'Network Error', url, { error: error.message });
        }
      }

      // Retry on network errors
      if (isRetriableError(error) && attempt < API_MAX_RETRY_ATTEMPTS && options.retry !== false) {
        const backoffDelay = Math.min(
          API_RETRY_BASE_DELAY_MS * Math.pow(2, attempt - 1),
          API_RETRY_MAX_DELAY_MS
        );
        await delay(backoffDelay);
        return this.executeWithRetry<T>(url, config, options, attempt + 1);
      }

      throw error;
    }
  }

  /**
   * GET request
   */
  async get<T>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>('GET', path, undefined, options);
  }

  /**
   * POST request
   */
  async post<T>(path: string, body: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>('POST', path, body, options);
  }

  /**
   * PUT request
   */
  async put<T>(path: string, body: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>('PUT', path, body, options);
  }

  /**
   * PATCH request
   */
  async patch<T>(path: string, body: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>('PATCH', path, body, options);
  }

  /**
   * DELETE request
   */
  async delete<T>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>('DELETE', path, undefined, options);
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
